// The note's small markdown vocabulary, parsed. No dependency, and that is the
// decision rather than an accident.
//
// WHY MARKDOWN AT ALL. The product already promises "Plain-file export -
// Markdown for notes" (`market-and-competitors.md`), so choosing markdown as the
// IN-APP vocabulary makes that export a byte copy instead of a lossy
// translation. Rich text or a style model of our own would put an asterisk on a
// promise that has already been made.
//
// WHY NOT A LIBRARY, MEASURED. The package comparison and dated bundle
// measurements live in `docs/research/notes-formatting.md` §4, which owns those
// numbers. The accepted implementation adds no dependency and needs no sanitizer
// because it emits REACT ELEMENTS: React escapes text by construction, so there
// is no string of HTML anywhere for anything to be injected into.
//
// WHAT IS DELIBERATELY NOT IN THE VOCABULARY: tables, callouts, footnotes,
// `==highlight==`, `%%comments%%`, `[[wikilinks]]` and `#tags`. The considered
// exclusions and their reasons live in `docs/research/notes-formatting.md` §10.
// Adding one is a product decision, not a parser change.
//
// PURE - no React, no DOM, no network - the same split `note-model.ts` keeps, so
// every rule here is readable and testable without a screen around it.
// `NoteMarkdown.tsx` turns what this returns into elements and decides nothing.

export type InlineMarker = '**' | '*' | '~~' | '`';

/** A run of text inside a line, after the inline markers have been read. */
export type Inline =
  | { kind: 'text'; text: string }
  | { kind: 'strong'; parts: Inline[] }
  | { kind: 'em'; parts: Inline[] }
  | { kind: 'strike'; parts: Inline[] }
  | { kind: 'code'; text: string }
  /**
   * `href` is null for a URL this file would not vouch for, and the renderer
   * then draws an `<a>` with NO href - inert, and still visible as the words
   * that were typed. That is how `[bad](javascript:alert(1))` is made harmless
   * without a sanitizer in the bundle: nothing is stripped and nothing is
   * rewritten, the link simply does not become a link.
   */
  | { kind: 'link'; href: string | null; parts: Inline[] };

export interface ListItem {
  /** null for an ordinary bullet; true/false for `- [x]` / `- [ ]`. */
  task: boolean | null;
  parts: Inline[];
  /**
   * The lists indented under this item, in the order they were written.
   *
   * MORE THAN ONE IS ORDINARY, which is why this is an array rather than the
   * one slot it reads like: bullets indented under an item's numbered steps are
   * a second list, not more steps, and an item that can only hold one child
   * list has nowhere to put them but into the first - renumbering somebody's
   * steps to do it.
   */
  lists: ListBlock[];
}

export interface ListBlock {
  kind: 'list';
  ordered: boolean;
  /** What `1.` started at, so a list that begins at 3 still reads 3, 4, 5. */
  start: number;
  items: ListItem[];
}

export type Block =
  /**
   * TWO LEVELS, NOT SIX. The day screen's own hierarchy is two deep and a note
   * is not a document; `###` and below stay literal text, which is honest -
   * nothing is silently swallowed.
   */
  | { kind: 'heading'; level: 1 | 2; parts: Inline[] }
  | { kind: 'para'; lines: Inline[][] }
  | { kind: 'quote'; lines: Inline[][] }
  | { kind: 'code'; text: string }
  | { kind: 'rule' }
  | ListBlock;

/**
 * A heading line, and THE ONE PLACE THIS CLIENT DECIDES WHAT A HEADING IS.
 *
 * TWO LEVELS, so `#{1,2}` and not `#{1,6}`. `parseNote` reads it to draw one and
 * `asProse` reads it to take one off, and they must agree: while `asProse`
 * stripped any hash run, a note beginning `### Team notes` LISTED as "Team
 * notes" while both the Write and Read views showed `### Team notes` - the list
 * and the renderer holding two opinions about what a heading is. `###` is
 * literal text in this vocabulary, so it stays visible everywhere.
 *
 * A third level arrives by changing this line, and both surfaces follow.
 */
const HEADING_LINE = /^\s*(#{1,2})[ \t]+(.*)$/;

/**
 * A fence delimiter, and THE ONE PLACE THIS CLIENT DECIDES WHERE CODE BEGINS
 * AND ENDS.
 *
 * `parseNote` reads it to hand a fence every line until it closes, and
 * `asProse` reads it to leave those lines exactly as they were typed - the same
 * agreement `HEADING_LINE` keeps above, and for the same reason: a `#` or a `-`
 * inside a pasted snippet is a character, not structure, and the preview, the
 * word count and the Read view may not hold two opinions about which.
 */
const FENCE_LINE = /^\s*```/;

/**
 * Whether a fenced block owns any part of the active textarea range.
 *
 * Fence delimiters belong to the block too. A selection that reaches into one
 * is wholly left to the browser rather than partly rewritten as prose, and an
 * unclosed fence owns through the end of the note just as `parseNote` does.
 */
export function fenceOwnsRange(body: string, start: number, end: number): boolean {
  const from = Math.max(0, Math.min(start, body.length));
  const to = Math.max(from, Math.min(end, body.length));
  const collapsed = from === to;
  const lines = body.split('\n');
  let offset = 0;
  let fenced = false;

  for (const line of lines) {
    const delimiter = FENCE_LINE.test(line);
    const owned = fenced || delimiter;
    const lineEnd = offset + line.length;
    const active = collapsed ? from >= offset && from <= lineEnd : from <= lineEnd && to > offset;
    if (owned && active) return true;
    if (delimiter) fenced = !fenced;
    offset = lineEnd + 1;
  }

  return false;
}

/**
 * A quote line, and THE ONE PLACE THIS CLIENT DECIDES WHERE A QUOTE STOPS.
 *
 * WHAT FOLLOWS THE MARKER IS THE QUOTE'S OWN WORDS, and nothing more is read off
 * it: a `quote` block holds `Inline[][]`, so `> - milk` is a quotation of the
 * characters `- milk` rather than a list inside a quotation. `parseNote` reads
 * this to draw one and `asProse` reads it to take the marker off, and they must
 * agree for the same reason the fence rule above is one line: the preview, the
 * word count and the Read view are three surfaces over one vocabulary.
 */
const QUOTE_LINE = /^\s*>\s?(.*)$/;

/** The schemes a link may carry. Everything else renders inert - see `Inline`. */
const SAFE_SCHEME = /^(https?:|mailto:)/i;

/** A list marker, with whatever it is indented by. */
const LIST_LINE = /^(\s*)(?:([-*+])|(\d{1,9})([.)]))\s+(.*)$/;

/**
 * `- [ ]` / `- [x]`, read off what the marker above left behind. The box may
 * end the line; when words follow it they need horizontal whitespace first, so
 * `- [x]buy milk` is a bullet whose words happen to start with a bracket rather
 * than a ticked task.
 *
 * EXPORTED BECAUSE `note-typing.ts` ASKS THE SAME QUESTION OF THE SAME LINE.
 * Two spellings of one vocabulary is two answers: a line the shortcuts treat as
 * a task and this file draws as a bullet is a note that behaves differently
 * depending on which view somebody is in. This file is the vocabulary's one
 * home, so the shortcuts read it from here.
 */
export const TASK_BOX = /^\[([ xX])\](?:[ \t]+(.*)|$)$/;

/** `---` on a line of its own. Three or more, so a row of dashes is still one. */
const RULE_LINE = /^\s*-{3,}\s*$/;

/** How many spaces one level of indent is worth. A tab counts as one level. */
const INDENT = 2;

function indentLevel(lead: string): number {
  let width = 0;
  for (const ch of lead) width += ch === '\t' ? INDENT : 1;
  return Math.floor(width / INDENT);
}

/**
 * What one line IS, and what its own words are once its marker is off.
 *
 * `text` IS THE WHOLE OF WHAT IS LEFT TO READ, on every kind that has one. A
 * heading's text is the heading, a quote's is the quotation, an item's is the
 * item - and none of them is looked at again, because in this vocabulary a
 * marker is only a marker at the START of a line and there is only ever one.
 */
type Line =
  | { kind: 'fence' }
  | { kind: 'blank' }
  | { kind: 'rule' }
  | { kind: 'heading'; level: 1 | 2; text: string }
  | { kind: 'quote'; text: string }
  | {
      kind: 'item';
      level: number;
      ordered: boolean;
      start: number;
      task: boolean | null;
      text: string;
    }
  | { kind: 'prose'; text: string };

/**
 * THE ONE PLACE A LINE IS READ, and the reason it exists rather than each
 * consumer asking the regexes in its own order.
 *
 * `parseNote` draws a line and `asProse` takes its markers off, and those two
 * answers have to be the same answer. While they each decided it themselves
 * they drifted three separate times - a fence's contents, a quote's contents,
 * and a heading reading `# 1. Overview`, which drew as a heading and previewed
 * as "Overview" because the preview went on to read the `1.` as a list. Each was
 * a branch that ended in one function and fell through in the other.
 *
 * So neither of them decides any more. This says what the line is and hands back
 * the words; a caller may use them or ignore them, and CANNOT re-read them,
 * which is what makes a fourth drift of the same kind unwritable rather than
 * merely unlikely.
 */
function classify(line: string): Line {
  if (FENCE_LINE.test(line)) return { kind: 'fence' };
  if (line.trim() === '') return { kind: 'blank' };
  if (RULE_LINE.test(line)) return { kind: 'rule' };

  const heading = HEADING_LINE.exec(line);
  if (heading !== null) {
    return { kind: 'heading', level: heading[1]?.length === 1 ? 1 : 2, text: heading[2] ?? '' };
  }

  const quoted = QUOTE_LINE.exec(line);
  if (quoted !== null) return { kind: 'quote', text: quoted[1] ?? '' };

  const listed = LIST_LINE.exec(line);
  if (listed !== null) {
    const ordered = listed[2] === undefined;
    const rest = listed[5] ?? '';
    // A NUMBERED item is never a task: `1. [ ]` is not in this vocabulary.
    const box = ordered ? null : TASK_BOX.exec(rest);
    return {
      kind: 'item',
      level: indentLevel(listed[1] ?? ''),
      ordered,
      start: ordered ? Number(listed[3]) : 1,
      task: box === null ? null : box[1] !== ' ',
      text: box === null ? rest : (box[2] ?? ''),
    };
  }

  return { kind: 'prose', text: line };
}

export function lineIsProse(line: string): boolean {
  return classify(line).kind === 'prose';
}

/** A line, cut at the point its own words begin. */
export interface LineContent {
  /** The indent and whatever block marker the line carries, exactly as typed. */
  marker: string;
  /** The line's own words - all of them, and nothing that structures the line. */
  content: string;
  emptyContentPrefix: '' | ' ';
}

/**
 * WHERE A LINE'S OWN WORDS START, for a shortcut that has to write AFTER the
 * marker rather than around it.
 *
 * EXPORTED FOR `note-typing.ts`, and read off `classify` rather than found
 * again. Cmd+B on a selected `- alpha` wrote `**- alpha**`, which stops being a
 * list at all - the bullet is no longer followed by a space, so the renderer
 * draws a bold paragraph with a dash in it. The shortcut has to know where the
 * marker ends, and there may not be a second regex saying where that is: two
 * definitions drift, and a shortcut and a renderer disagreeing about what a list
 * is is the exact defect this file exists to make unwritable.
 *
 * IT IS A SLICE AND NOT A MATCH. `classify` already hands back the line's text,
 * and every branch's text is a SUFFIX of the line it came from - so what
 * precedes it is the marker, whatever kind of marker it was, including the ones
 * added later. A line with no words in it - blank, a rule, a fence delimiter -
 * is all marker and no content, which is the honest answer: there is nothing
 * there to put emphasis on.
 */
export function splitLine(line: string): LineContent {
  const role = classify(line);
  const text = 'text' in role ? role.text : '';
  const emptyContentPrefix =
    role.kind === 'item' && role.task !== null && text === '' && !/[ \t]$/.test(line) ? ' ' : '';
  return {
    marker: line.slice(0, line.length - text.length),
    content: text,
    emptyContentPrefix,
  };
}

/**
 * A note, as blocks.
 *
 * ONE PASS, LINE BY LINE, and the line is the unit: this vocabulary has no
 * construct that reaches across a blank line, so there is nothing to look ahead
 * for. That is what keeps the whole thing small.
 */
export function parseNote(body: string): Block[] {
  const lines = body.split('\n');
  const blocks: Block[] = [];
  let paragraph: Inline[][] | null = null;
  let quote: Inline[][] | null = null;
  let list: {
    block: ListBlock;
    levels: Array<{ block: ListBlock; indent: number }>;
  } | null = null;

  const closeAll = () => {
    paragraph = null;
    quote = null;
    list = null;
  };

  for (let i = 0; i < lines.length; i += 1) {
    const role = classify(lines[i] ?? '');

    // A FENCE OWNS EVERY LINE UNTIL IT CLOSES, which is the one place this
    // parser looks past the current line - and it has to, or a `#` inside a
    // pasted snippet becomes a heading. An unclosed fence runs to the end of
    // the note rather than being abandoned: somebody mid-paste has typed the
    // opening fence and nothing else, and re-drawing their whole note as
    // headings while they type the rest is the worse answer.
    if (role.kind === 'fence') {
      closeAll();
      const collected: string[] = [];
      i += 1;
      for (; i < lines.length; i += 1) {
        const inner = lines[i] ?? '';
        if (FENCE_LINE.test(inner)) break;
        collected.push(inner);
      }
      blocks.push({ kind: 'code', text: collected.join('\n') });
      continue;
    }

    if (role.kind === 'blank') {
      closeAll();
      continue;
    }

    if (role.kind === 'rule') {
      closeAll();
      blocks.push({ kind: 'rule' });
      continue;
    }

    if (role.kind === 'heading') {
      closeAll();
      blocks.push({ kind: 'heading', level: role.level, parts: parseInline(role.text) });
      continue;
    }

    if (role.kind === 'quote') {
      paragraph = null;
      list = null;
      if (quote === null) {
        quote = [];
        blocks.push({ kind: 'quote', lines: quote });
      }
      quote.push(parseInline(role.text));
      continue;
    }

    if (role.kind === 'item') {
      paragraph = null;
      quote = null;
      const ordered = role.ordered;

      const item: ListItem = {
        task: role.task,
        parts: parseInline(role.text),
        lists: [],
      };
      const openList = (): ListBlock => ({
        kind: 'list',
        ordered,
        start: role.start,
        items: [],
      });

      if (list === null) {
        const block = openList();
        block.items.push(item);
        blocks.push(block);
        list = { block, levels: [{ block, indent: role.level }] };
        continue;
      }

      let depth = list.levels.findIndex((level) => role.level <= level.indent);
      if (depth === -1) {
        const outer = list.levels[list.levels.length - 1]?.block ?? list.block;
        const parent = outer.items[outer.items.length - 1];
        if (parent !== undefined) {
          const nested = openList();
          nested.items.push(item);
          parent.lists.push(nested);
          list.levels.push({ block: nested, indent: role.level });
          continue;
        }
        depth = list.levels.length - 1;
      }

      const level = list.levels[depth];
      if (level === undefined) continue;
      level.indent = role.level;
      list.levels.length = depth + 1;

      // A bullet under a numbered list is a DIFFERENT list, not an item of it -
      // the two say different things, and folding one into the other would
      // silently renumber somebody's steps. ASKED AT THE DEPTH THE INDENT
      // RESOLVED TO, not only at the root: a list nested under an item is just
      // as able to disagree with what follows it, and it is the one somebody
      // reading the rendered note would see renumbered.
      let target = level.block;
      if (target.ordered !== ordered) {
        const fresh = openList();
        const holder = depth === 0 ? null : (list.levels[depth - 1]?.block ?? null);
        const parent = holder === null ? null : (holder.items[holder.items.length - 1] ?? null);
        if (parent === null) {
          // At the note's left margin there is nothing to hang it on, so the
          // whole list closes and a new one starts beside it.
          blocks.push(fresh);
          list = { block: fresh, levels: [{ block: fresh, indent: role.level }] };
        } else {
          // Deeper in, it belongs to the item this level was opened under -
          // beside the list it disagrees with, not instead of it.
          parent.lists.push(fresh);
          list.levels[depth] = { block: fresh, indent: role.level };
        }
        target = fresh;
      }
      target.items.push(item);
      continue;
    }

    // Anything else is prose. A SINGLE NEWLINE IS A LINE BREAK here, which is
    // markdown's "strict line breaks" turned off and Obsidian's own default:
    // somebody typing two lines in a notebook means two lines, and collapsing
    // them the way a document renderer would is the change they did not ask for.
    quote = null;
    list = null;
    if (paragraph === null) {
      paragraph = [];
      blocks.push({ kind: 'para', lines: paragraph });
    }
    paragraph.push(parseInline(role.text));
  }

  return blocks;
}

/** Every inline marker, tried at the earliest position any of them matches. */
const INLINE = [
  { open: '`', close: '`', kind: 'code' as const },
  { open: '****', close: '****', kind: 'strong-strong' as const },
  { open: '***', close: '***', kind: 'strong-em' as const },
  { open: '**', close: '**', kind: 'strong' as const },
  { open: '~~', close: '~~', kind: 'strike' as const },
  { open: '*', close: '*', kind: 'em' as const },
];

/**
 * HOW FAR A BARE ADDRESS RUNS.
 *
 * PARENTHESES ARE BALANCED, which is CommonMark's rule and is here for one
 * everyday paste: `https://en.wikipedia.org/wiki/Mercury_(planet)`. While the
 * address stopped at the first `)`, that link's `href` was
 * `…/Mercury_(planet` - a dead address - with a stray `)` drawn after the words.
 * An UNBALANCED `)` still ends it, which is what keeps the `)` in
 * `(see https://x.test/a)` part of the sentence, and what lets `[doc](url)` find
 * its own closer.
 *
 * READ BY `isUrl` IN `note-typing.ts` TOO. Half of that defect was the paste
 * accepting an address the renderer then truncated - two opinions about one
 * rule, the same shape this file already closed for heading levels. So the
 * shortcut asks this before it writes a link, and the two cannot disagree.
 */
export function scanAddress(text: string): { length: number; openParentheses: number } {
  let depth = 0;
  let i = 0;
  for (; i < text.length; i += 1) {
    const ch = text[i] ?? '';
    if (isAddressBoundary(ch)) break;
    if (ch === '(') depth += 1;
    else if (ch === ')') {
      if (depth === 0) break;
      depth -= 1;
    }
  }
  return { length: i, openParentheses: depth };
}

/**
 * One line's text, as runs.
 *
 * SCANS FOR THE EARLIEST MARKER rather than running one regex per form, so
 * `**bold**` is never read as two `*italic*` markers and a `*` with no partner
 * stays the character somebody typed. Nesting is by recursion, so
 * `**a *b* c**` works and costs nothing extra.
 */
export function parseInline(text: string): Inline[] {
  const context = inlineContext(text);
  return parseInlineText(context, 0, text.length, true);
}

export function inlineText(text: string): string {
  const out: string[] = [];
  const pending = [...parseInline(text)].reverse();

  while (pending.length > 0) {
    const part = pending.pop();
    if (part === undefined) continue;
    if (part.kind === 'text' || part.kind === 'code') {
      out.push(part.text);
    } else {
      for (let i = part.parts.length - 1; i >= 0; i -= 1) {
        const child = part.parts[i];
        if (child !== undefined) pending.push(child);
      }
    }
  }

  return out.join('');
}

export function inlineIsFullyFormatted(text: string, marker: InlineMarker): boolean {
  const parts = parseInline(text);
  if (parts.length !== 1) return false;
  const part = parts[0];
  if (part === undefined) return false;

  switch (marker) {
    case '**':
      return part.kind === 'strong';
    case '*':
      return part.kind === 'em';
    case '~~':
      return part.kind === 'strike';
    case '`':
      return part.kind === 'code';
  }
}

interface InlineLink {
  end: number;
  labelStart: number;
  labelEnd: number;
  urlStart: number;
  urlEnd: number;
}

interface InlineSpan {
  start: number;
  end: number;
}

interface InlineLinkIndex {
  byStart: ReadonlyMap<number, InlineLink>;
  spans: readonly InlineSpan[];
}

type InlineCodeIndex = readonly InlineSpan[];
type ClosingCache = Map<string, number>;

interface InlineContext {
  text: string;
  links: InlineLinkIndex;
  codes: InlineCodeIndex;
  closers: ClosingCache;
}

function closerKey(marker: (typeof INLINE)[number], start: number): string {
  return `${marker.kind}:${start}`;
}

function isAddressBoundary(character: string): boolean {
  return /\s/.test(character) || character === '<' || character === '>';
}

function matchingClosers(
  text: string,
  open: string,
  close: string,
  boundary?: (character: string) => boolean,
): Int32Array {
  const matches = new Int32Array(text.length);
  matches.fill(-1);
  const stack: number[] = [];

  for (let at = 0; at < text.length; at += 1) {
    const character = text[at] ?? '';
    if (boundary?.(character) === true) {
      stack.length = 0;
    } else if (character === open) {
      stack.push(at);
    } else if (character === close) {
      const start = stack.pop();
      if (start !== undefined) matches[start] = at;
    }
  }

  return matches;
}

function indexInlineLinks(text: string): InlineLinkIndex {
  const labelEnds = matchingClosers(text, '[', ']');
  const urlEnds = matchingClosers(text, '(', ')', isAddressBoundary);
  const byStart = new Map<number, InlineLink>();
  const spans: InlineSpan[] = [];

  for (let start = 0; start < text.length; start += 1) {
    const labelEnd = labelEnds[start] ?? -1;
    const urlOpen = labelEnd + 1;
    const urlEnd = urlEnds[urlOpen] ?? -1;
    if (labelEnd < 0 || text[urlOpen] !== '(' || urlEnd < 0) continue;
    const link = {
      end: urlEnd + 1,
      labelStart: start + 1,
      labelEnd,
      urlStart: urlOpen + 1,
      urlEnd,
    };
    byStart.set(start, link);

    const previous = spans[spans.length - 1];
    if (previous !== undefined && start < previous.end) {
      previous.end = Math.max(previous.end, link.end);
    } else {
      spans.push({ start, end: link.end });
    }
  }

  return { byStart, spans };
}

function inlineLinkAt(links: InlineLinkIndex, start: number): InlineLink | null {
  return links.byStart.get(start) ?? null;
}

function indexInlineCode(text: string, links: InlineLinkIndex): InlineCodeIndex {
  const spans: InlineSpan[] = [];
  let at = 0;

  while (at < text.length) {
    const link = inlineLinkAt(links, at);
    if (link !== null) {
      at = link.end;
      continue;
    }
    if (text[at] !== '`') {
      at += 1;
      continue;
    }
    const close = text.indexOf('`', at + 1);
    if (close === -1) break;
    spans.push({ start: at, end: close + 1 });
    at = close + 1;
  }

  return spans;
}

function inlineCodeContaining(codes: InlineCodeIndex, position: number): InlineSpan | null {
  let low = 0;
  let high = codes.length - 1;
  let candidate: InlineSpan | undefined;

  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const span = codes[middle];
    if (span === undefined) break;
    if (span.start <= position) {
      candidate = span;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }

  return candidate !== undefined && position < candidate.end ? candidate : null;
}

function bareAddressAt(
  text: string,
  start: number,
  end = text.length,
  rangeStart = 0,
): InlineSpan | null {
  const prefix = text.slice(start, Math.min(start + 8, end));
  if (!/^https?:\/\//i.test(prefix) || !atWordStart(text, start, rangeStart)) return null;
  const rest = text.slice(start, end);
  const address = rest.slice(0, scanAddress(rest).length).replace(/[.,;:!?]+$/, '');
  return address === '' ? null : { start, end: start + address.length };
}

function inlineLinkContaining(
  links: InlineLinkIndex,
  from: number,
  position: number,
): InlineSpan | null {
  let low = 0;
  let high = links.spans.length - 1;
  let candidate: InlineSpan | undefined;

  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const span = links.spans[middle];
    if (span === undefined) break;
    if (span.start <= position) {
      candidate = span;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }

  return candidate !== undefined && candidate.start >= from && position < candidate.end
    ? candidate
    : null;
}

function inlineContext(text: string): InlineContext {
  const links = indexInlineLinks(text);
  return {
    text,
    links,
    codes: indexInlineCode(text, links),
    closers: new Map(),
  };
}

export function linkLabelIsSupported(label: string): boolean {
  const serialized = `[${label}]()`;
  const link = inlineLinkAt(indexInlineLinks(serialized), 0);
  return (
    link !== null &&
    link.end === serialized.length &&
    serialized.slice(link.labelStart, link.labelEnd) === label
  );
}

export function linkOwnsRange(body: string, start: number, end: number): boolean {
  const from = Math.max(0, Math.min(start, body.length));
  const to = Math.max(from, Math.min(end, body.length));
  const collapsed = from === to;
  const lines = body.split('\n');
  let offset = 0;
  let fenced = false;

  for (const line of lines) {
    if (FENCE_LINE.test(line)) {
      fenced = !fenced;
      offset += line.length + 1;
      continue;
    }
    if (!fenced) {
      const links = indexInlineLinks(line);
      let at = 0;
      while (at < line.length) {
        if (line[at] === '`') {
          const close = line.indexOf('`', at + 1);
          if (close !== -1) {
            at = close + 1;
            continue;
          }
        }
        const link = inlineLinkAt(links, at);
        if (link !== null) {
          const linkStart = offset + at;
          const linkEnd = offset + link.end;
          const active = collapsed
            ? from >= linkStart && from < linkEnd
            : from < linkEnd && to > linkStart;
          if (active) return true;
          at = link.end;
          continue;
        }
        at += 1;
      }
    }
    offset += line.length + 1;
  }

  return false;
}

function nestedEmphasisClosesAt(context: InlineContext, start: number, end: number): boolean {
  const emphasis = INLINE.find((candidate) => candidate.kind === 'em');
  if (emphasis === undefined) return false;

  const { text, links } = context;
  let at = start;
  while (at < end) {
    if (text[at] === '`') {
      const close = text.indexOf('`', at + 1);
      if (close !== -1 && close < end) {
        at = close + 1;
        continue;
      }
    }

    const link = inlineLinkAt(links, at);
    if (link !== null && link.end <= end) {
      at = link.end;
      continue;
    }

    const after = text[at + 1];
    if (
      text[at] === '*' &&
      text[at - 1] !== '*' &&
      after !== '*' &&
      after !== undefined &&
      !/\s/.test(after) &&
      closingMarkerAt(context, at + 1, emphasis, end + emphasis.close.length) === end
    ) {
      return true;
    }
    at += 1;
  }

  return false;
}

function isDelimiterWhitespace(character: string | undefined): boolean {
  return character === undefined || /\s/u.test(character);
}

function isDelimiterPunctuation(character: string | undefined): boolean {
  return character !== undefined && /[\p{P}\p{S}]/u.test(character);
}

function delimiterRoles(
  text: string,
  start: number,
  marker: (typeof INLINE)[number],
): { canOpen: boolean; canClose: boolean } {
  const before = text[start - 1];
  const after = text[start + marker.close.length];
  const leftFlanking =
    !isDelimiterWhitespace(after) &&
    (!isDelimiterPunctuation(after) ||
      isDelimiterWhitespace(before) ||
      isDelimiterPunctuation(before));
  const rightFlanking =
    !isDelimiterWhitespace(before) &&
    (!isDelimiterPunctuation(before) ||
      isDelimiterWhitespace(after) ||
      isDelimiterPunctuation(after));

  return { canOpen: leftFlanking, canClose: rightFlanking };
}

function closingMarkerAt(
  context: InlineContext,
  start: number,
  marker: (typeof INLINE)[number],
  limit = context.text.length,
): number {
  const { text, links, codes, closers } = context;
  const key = closerKey(marker, start);
  const cached = closers.get(key);
  if (cached !== undefined && (cached === -1 || cached + marker.close.length <= limit)) {
    return cached;
  }

  const nestedStarts: number[] = [];
  let at = start;

  while (at < limit) {
    const found = text.indexOf(marker.close, at);
    if (found === -1 || found + marker.close.length > limit) {
      if (limit === text.length) {
        closers.set(key, -1);
        for (const nestedStart of nestedStarts) {
          closers.set(closerKey(marker, nestedStart), -1);
        }
      }
      return -1;
    }
    if (marker.kind === 'code') {
      closers.set(key, found);
      return found;
    }

    const containingLink = inlineLinkContaining(links, start, found);
    if (containingLink !== null && containingLink.end <= limit) {
      at = containingLink.end;
      continue;
    }

    const containingCode = inlineCodeContaining(codes, found);
    if (containingCode !== null && containingCode.start >= start && containingCode.end <= limit) {
      at = containingCode.end;
      continue;
    }

    const { canOpen, canClose } = delimiterRoles(text, found, marker);

    if (marker.kind === 'em' && text.startsWith('**', found)) {
      const strong = INLINE.find((candidate) => candidate.kind === 'strong');
      if (strong !== undefined) {
        const strongRoles = delimiterRoles(text, found, strong);
        if (strongRoles.canOpen && !strongRoles.canClose) {
          const nestedEnd = closingMarkerAt(context, found + strong.open.length, strong, limit);
          if (nestedEnd !== -1) {
            at = nestedEnd + strong.close.length;
            continue;
          }
        }
      }
    }

    if (
      marker.kind === 'strong' &&
      text.startsWith('***', found) &&
      nestedEmphasisClosesAt(context, start, found)
    ) {
      closers.set(key, found + 1);
      return found + 1;
    }

    if (canClose) {
      const nestedStart = nestedStarts.pop();
      if (nestedStart === undefined) {
        closers.set(key, found);
        return found;
      }
      closers.set(closerKey(marker, nestedStart), found);
    } else if (canOpen) {
      nestedStarts.push(found + marker.open.length);
    }

    at = found + marker.close.length;
  }

  if (limit === text.length) {
    closers.set(key, -1);
    for (const nestedStart of nestedStarts) {
      closers.set(closerKey(marker, nestedStart), -1);
    }
  }
  return -1;
}

interface InlineBoundary {
  fullStart: number;
  contentStart: number;
  contentEnd: number;
  fullEnd: number;
  contentMode: 'label' | 'nested' | 'toggle-only' | 'literal';
}

function inlineBoundaries(
  context: InlineContext,
  start = 0,
  end = context.text.length,
  offset = 0,
  boundaries: InlineBoundary[] = [],
  allowLinks = true,
): InlineBoundary[] {
  const { text, links } = context;
  let at = start;

  while (at < end) {
    const link = allowLinks ? inlineLinkAt(links, at) : null;
    if (link !== null && link.end <= end) {
      boundaries.push({
        fullStart: offset + at,
        contentStart: offset + link.labelStart,
        contentEnd: offset + link.labelEnd,
        fullEnd: offset + link.end,
        contentMode: 'label',
      });
      inlineBoundaries(context, link.labelStart, link.labelEnd, offset, boundaries, false);
      at = link.end;
      continue;
    }

    const address = allowLinks ? bareAddressAt(text, at, end, start) : null;
    if (address !== null) {
      boundaries.push({
        fullStart: offset + address.start,
        contentStart: offset + address.start,
        contentEnd: offset + address.end,
        fullEnd: offset + address.end,
        contentMode: 'literal',
      });
      at = address.end;
      continue;
    }

    const marker = INLINE.find(
      (candidate) => at + candidate.open.length <= end && text.startsWith(candidate.open, at),
    );
    if (marker === undefined) {
      at += 1;
      continue;
    }

    const contentStart = at + marker.open.length;
    const contentEnd =
      marker.kind !== 'code' && /^\s/.test(text.slice(contentStart, contentStart + 1))
        ? -1
        : closingMarkerAt(context, contentStart, marker, end);
    if (contentEnd === -1 || contentEnd === contentStart) {
      at += 1;
      continue;
    }

    const fullEnd = contentEnd + marker.close.length;
    boundaries.push({
      fullStart: offset + at,
      contentStart: offset + contentStart,
      contentEnd: offset + contentEnd,
      fullEnd: offset + fullEnd,
      contentMode:
        marker.kind === 'code'
          ? 'literal'
          : marker.kind === 'strong-em' || marker.kind === 'strong-strong'
            ? 'toggle-only'
            : 'nested',
    });
    if (marker.kind !== 'code') {
      inlineBoundaries(context, contentStart, contentEnd, offset, boundaries, allowLinks);
    }
    at = fullEnd;
  }

  return boundaries;
}

export function inlineOwnsPartialRange(body: string, start: number, end: number): boolean {
  const from = Math.max(0, Math.min(start, body.length));
  const to = Math.max(from, Math.min(end, body.length));
  let offset = 0;

  for (const line of body.split('\n')) {
    const context = inlineContext(line);
    for (const boundary of inlineBoundaries(context, 0, line.length, offset)) {
      const { fullStart, contentStart, contentEnd, fullEnd, contentMode } = boundary;
      if (from === to) {
        if (from > fullStart && from < fullEnd) return true;
        continue;
      }
      if (to <= fullStart || from >= fullEnd) continue;
      if (from <= fullStart && to >= fullEnd) continue;
      if (from === contentStart && to === contentEnd && contentMode !== 'literal') continue;
      if (from >= contentStart && to <= contentEnd && contentMode === 'label') continue;
      if (from > contentStart && to < contentEnd && contentMode === 'nested') continue;
      return true;
    }
    offset += line.length + 1;
  }

  return false;
}

function parseInlineText(
  context: InlineContext,
  start: number,
  end: number,
  allowLinks: boolean,
): Inline[] {
  const parts: Inline[] = [];
  const { text, links } = context;
  let plain = '';

  const flush = () => {
    if (plain !== '') parts.push({ kind: 'text', text: plain });
    plain = '';
  };

  let i = start;
  while (i < end) {
    // `[text](url)` first: its own body may hold any of the markers below, and
    // a `*` inside a link's words must not close a run that started outside it.
    // Indexed rather than matched, because a regex cannot balance parentheses.
    if (allowLinks && text[i] === '[') {
      const link = inlineLinkAt(links, i);
      if (link !== null && link.end <= end) {
        const url = text.slice(link.urlStart, link.urlEnd);
        flush();
        parts.push({
          kind: 'link',
          href: SAFE_SCHEME.test(url) ? url : null,
          parts: parseInlineText(context, link.labelStart, link.labelEnd, false),
        });
        i = link.end;
        continue;
      }
    }

    // A URL nobody wrapped in anything. Only at a word boundary, so the tail of
    // `(https://x.test/a)` inside a link's parens is never picked up twice.
    if (allowLinks) {
      const address = bareAddressAt(text, i, end, start);
      if (address !== null) {
        // Trailing punctuation belongs to the sentence, not to the address -
        // "see https://x.test/a." is a link and a full stop.
        const url = text.slice(address.start, address.end);
        flush();
        parts.push({ kind: 'link', href: url, parts: [{ kind: 'text', text: url }] });
        i = address.end;
        continue;
      }
    }

    const marker = INLINE.find(
      (candidate) => i + candidate.open.length <= end && text.startsWith(candidate.open, i),
    );
    if (marker !== undefined) {
      const from = marker.open.length;
      // NO SPACE AGAINST THE INSIDE OF A MARKER, which is markdown's own
      // flanking rule and is here for one everyday sentence: `2 * 3 * 4` is
      // multiplication, not italics. A close with a space before it is not a
      // close, so the search moves on to the next one. Code spans are exempt -
      // a backtick run may hold whatever spacing the snippet had.
      const close =
        marker.kind !== 'code' && /^\s/.test(text.slice(i + from, i + from + 1))
          ? -1
          : closingMarkerAt(context, i + from, marker, end);
      // An UNCLOSED marker is the character somebody typed, not a run that
      // swallows the rest of the line. Half of `*` typed is the ordinary state
      // of a line being written.
      if (close !== -1 && close !== i + from) {
        flush();
        let run: Inline;
        switch (marker.kind) {
          case 'code':
            run = { kind: 'code', text: text.slice(i + from, close) };
            break;
          case 'strong-strong':
            run = {
              kind: 'strong',
              parts: [
                {
                  kind: 'strong',
                  parts: parseInlineText(context, i + from, close, allowLinks),
                },
              ],
            };
            break;
          case 'strong-em':
            run = {
              kind: 'strong',
              parts: [
                {
                  kind: 'em',
                  parts: parseInlineText(context, i + from, close, allowLinks),
                },
              ],
            };
            break;
          default:
            run = {
              kind: marker.kind,
              parts: parseInlineText(context, i + from, close, allowLinks),
            };
        }
        parts.push(run);
        i = close + marker.close.length;
        continue;
      }
    }

    plain += text[i];
    i += 1;
  }

  flush();
  return parts;
}

function atWordStart(text: string, at: number, rangeStart = 0): boolean {
  if (at === rangeStart) return true;
  return /[\s(]/.test(text[at - 1] ?? '');
}

/**
 * The note as prose: the same words, with the LINE-LEADING structure taken off.
 *
 * WHAT IT IS FOR, and it is two things that would otherwise each grow a copy of
 * this vocabulary. The list's two-line preview flattens a whole note onto one
 * line, so `# Monday plan` and `- [ ] call the bank` would pepper the middle of
 * a sentence with markers that had nothing left to structure; and the word count
 * under the editor read `- [ ] call` as four words, because `-`, `[` and `]` are
 * whitespace-separated tokens.
 *
 * INLINE MARKERS ARE LEFT ALONE, deliberately. `**Deadline**` still reads as the
 * word with emphasis on it, and it is one token either way, so taking it out
 * buys nothing and would make this the second place in the client that decides
 * what `**` means.
 *
 * IT TAKES `classify`'S WORD FOR EVERY LINE and reads none of them itself, which
 * is the whole of how it stays in step with `parseNote`. A heading's `text` is
 * the heading, so `# 1. Overview` keeps its `1.`; a quote's is the quotation, so
 * `> - milk` keeps its dash. Both were wrong here while this asked the regexes
 * itself, in the same way and for the same reason, and neither is expressible
 * now: there is nowhere left to strip a second marker off a line.
 *
 * A FENCE IS THE ONE THING IT STILL WALKS, because a fence owns lines it has not
 * been handed yet - the same look-ahead `parseNote` makes, and the delimiters go
 * the way a rule does. An unclosed fence runs to the end of the note there too.
 */
export function asProse(body: string): string {
  const lines = body.split('\n');
  const out: string[] = [];

  for (let i = 0; i < lines.length; i += 1) {
    const role = classify(lines[i] ?? '');

    if (role.kind === 'fence') {
      out.push('');
      i += 1;
      for (; i < lines.length; i += 1) {
        const inner = lines[i] ?? '';
        if (FENCE_LINE.test(inner)) break;
        out.push(inner);
      }
      if (i < lines.length) out.push('');
      continue;
    }

    // A rule and a blank line are structure with no words in them at all.
    out.push(role.kind === 'blank' || role.kind === 'rule' ? '' : role.text);
  }

  return out.join('\n');
}
